import React, { ChangeEvent } from 'react';
import { useQuery } from '@apollo/client';
import { ITag } from '@fluentui/react';
import { Field, Select, FluentProvider, Spinner, webLightTheme } from '@fluentui/react-components';
import IDropdownOption from '../../../utils/types/IDropdownOption';
import { GET_STATE_PROVINCE_REGION_FOR_COUNTRY } from '../../../utils/statApi/CompanyCreationApi';
import LoadingErrorMessage from '../../common/errorContent/LoadingErrorMessage';

interface IStateProvinceRegionDropDownProps {
  selectedCountry: ITag[];
  defaultValue: string;
  onChange: (
    event: ChangeEvent<HTMLSelectElement>,
    option?: IDropdownOption,
    index?: number,
  ) => void;
  disabled: boolean;
  label?: React.ReactNode;
}

const StateProvinceRegionDropDown = ({
  selectedCountry,
  defaultValue,
  onChange,
  disabled,
  label,
}: IStateProvinceRegionDropDownProps): JSX.Element => {
  const countryCode =
    selectedCountry && selectedCountry.length > 0 ? selectedCountry[0].key.toString() : '--';

  const { data, loading, error } = useQuery(GET_STATE_PROVINCE_REGION_FOR_COUNTRY, {
    variables: {
      countryCode,
    },
  });

  const options = data?.regionsByCountry || [];

  return (
    <>
      {error && <LoadingErrorMessage loading={loading} error={error} />}
      {loading && !error && (
        <>
          <br />
          <Spinner label="Loading Regions" />
        </>
      )}
      {!loading && !error && countryCode === '--' && options.length === 0 && (
        <p>Please select a country</p>
      )}
      {!loading && !error && options.length > 0 && (
        <FluentProvider theme={webLightTheme}>
          <Field label={label || 'State/Province/Region'}>
            <Select
              defaultValue={defaultValue}
              onChange={(event, selected) => {
                const selectedOption = options.find(
                  (opt: IDropdownOption) => opt.key === selected.value,
                );
                onChange(event, selectedOption, options.indexOf(selectedOption));
              }}
              disabled={disabled}
            >
              {options.map((option: IDropdownOption) => (
                <option key={option.key} value={option.key}>
                  {option.text}
                </option>
              ))}
            </Select>
          </Field>
        </FluentProvider>
      )}
    </>
  );
};

export default StateProvinceRegionDropDown;
